import { useEffect, useState } from 'react';

import { Box, LinearProgress, Typography } from '@mui/material';

import { useAppSelector } from '@/hooks/redux-hooks';
import useUserDetails from '@/hooks/useUserDetails';
import { formatDaysLeft } from '@/utils/formatString';

const ExpiryCountdown = () => {
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const { userAccess, subscription } = useAppSelector(state => state.auth);

  const { isTrial, timeToExpire, isUserExpired } = useUserDetails();

  useEffect(() => {
    if (!timeToExpire) return;
    if (isUserExpired) {
      setDaysLeft(0);
      return;
    }

    if (
      timeToExpire.days > 0 &&
      timeToExpire.hours < 0 &&
      timeToExpire.minutes < 0 &&
      timeToExpire.seconds < 60
    ) {
      setDaysLeft(timeToExpire.days);
      return;
    }

    setDaysLeft(timeToExpire.days + 1); // Adding 1 to include hours, minutes left in the day
  }, [timeToExpire, isUserExpired]);

  if (daysLeft === null) return null;
  if (!isTrial && daysLeft > 14) return null;

  const progressPercentage = (Math.min(daysLeft, 14) / 14) * 100;
  const plan = subscription?.plan ?? userAccess;

  return (
    <Box
      sx={{
        width: '100%',
        height: '67px',
        pt: '8px',
        pr: '16px',
        pb: '8px',
        pl: '16px',
        gap: '4px',
        background: 'var(--GX-Response, #1A224D);',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignSelf: 'center',
        mb: '24px'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography
          sx={{
            font: 'Cairo',
            fontSize: '14px',
            color: 'white',
            textAlign: 'left'
          }}
          variant='body2'
        >
          {plan} {isTrial ? 'trial' : 'subscription'}
        </Typography>
        <Typography
          sx={{
            font: 'Cairo',
            fontSize: '14px',
            color: 'white',
            textAlign: 'right'
          }}
          variant='body2'
        >
          {formatDaysLeft(daysLeft)} left
        </Typography>
      </Box>
      <LinearProgress
        sx={{
          mt: '3px',
          height: '4px',
          backgroundColor: 'rgba(255, 255, 255, 0.3)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: 'primary.main'
          }
        }}
        value={progressPercentage}
        variant='determinate'
      />
    </Box>
  );
};

export default ExpiryCountdown;
